import { IEntity } from '../interfaces/entity';
import { Category } from './lookup-category';
import { Layout } from './layout';
import { SubjectType } from './subject-type';
import { Template } from './template';
import { User } from './user';
import { SubjectLink } from './subject-link';
import { Assessment } from './assessment';
import { Marker } from './marker';
import { Referral } from './referral';
import { enumReviewStatus, Review } from './review';
import { CurrencyPipe } from '@angular/common';
import { Organisation } from './organisations';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

export const SUBJECTFIXEDFIELDS = [
  'sequenceid',
  'reference',
  'summary',
  'categoryid',
  'labels',
  'datecreated',
  'retentionDate',
  'createdbyid',
  'stateid',
  'userid',
  'managerid',
  'primarylink',
  'markers',
  'assessmentscore',
  'organisationid',
  'hasLocation',
];

export interface ISubjectList {
  items: Subject[];
  totalCount: number;
}

export class Subject implements IEntity {
  id: number;
  uid: string;
  sequenceId: number;
  reference: string;
  summary: string;
  categoryId: number;
  category: Category;
  labels: string;
  dateCreated: string;
  subjectTypeId: number;
  subjectType: SubjectType;
  createdById: number;
  createdBy: User;
  stateId: number;
  archived: boolean;
  dateArchived: string;
  retentionDate: string;
  retentionId: number;
  subjectData: SubjectData;
  linkedSubjectMarkers: SubjectMarker[];
  subjectAssessments: SubjectAssessment[];
  subjectLinks: SubjectLink[];
  subjectReferrals: SubjectReferral[];
  subjectReviews: SubjectReview[];
  subjectMarkers: SubjectMarker[];
  subjectOrganisations: SubjectOrganisation[];
  subjectTemplates: SubjectTemplate[];
  subjectUsers: SubjectUser[];
  restricted: boolean;
  reviewStatus: number;
  reviewDate: string;
  isReferral: boolean;
  hasReferral: boolean;
  isPrimary: boolean;
  canDelete: boolean;
  referral: Referral;
  assessmentScore: number;
  assessmentThreshold: number;
  assessmentReviewStatus: number;
  hasCasePreparationFile: boolean;

  hasLocation: boolean;
  locationChecked: boolean;
  latitude: number;
  longitude: number;
  linkedLocations: LinkedLocation[];

  dfdeadlineDaysLeftId: number = -1;

  constructor(subject?) {
    subject = subject || {};
    this.id = subject.id;
    this.uid = subject.uid;
    this.reference = subject.reference || '';
    this.summary = subject.summary || '';
    this.categoryId = subject.categoryId;
    this.category = subject.category;
    this.labels = subject.labels || '';
    this.createdById = subject.createdById;
    this.createdBy = subject.createdBy;
    this.dateCreated = subject.dateCreated || '';
    this.retentionDate = subject.retentionDate || '';
    this.retentionId = subject.retentionId;
    this.subjectTypeId = subject.subjectTypeId;
    this.subjectType = subject.subjectType;
    this.subjectData = subject.subjectData || new SubjectData();
    this.subjectData.subjectId = subject.id;
    this.subjectData.subjectTypeId = subject.subjectTypeId;
    if (subject.subjectData) {
      this.subjectData.data = subject.subjectData.data;
      this.subjectData.displayValues = subject.subjectData.displayValues;
    }
    this.linkedSubjectMarkers = subject.linkedSubjectMarkers || [];
    this.subjectAssessments = subject.subjectAssessments || [];
    this.subjectLinks = subject.subjectLinks || [];
    this.subjectReferrals = subject.subjectReferrals || [];
    this.subjectMarkers = subject.subjectMarkers || [];
    this.subjectOrganisations = subject.subjectOrganisations || [];
    this.subjectReviews = subject.subjectReviews || [];
    this.subjectTemplates = subject.subjectTemplates || [];
    this.subjectUsers = subject.subjectUsers || [];
    this.archived = subject.archived || false;
    this.dateArchived = subject.dateArchived;
    this.restricted = subject.restricted || false;
    this.sequenceId = subject.sequenceId;
    this.stateId = subject.stateId;
    this.reviewStatus = subject.reviewStatus || enumReviewStatus.Pending;
    this.reviewDate = subject.reviewDate;
    this.isReferral = subject.isReferral || false;
    this.hasReferral = subject.hasReferral || false;
    this.isPrimary = subject.isPrimary || false;
    this.canDelete = subject.canDelete;
    this.referral = subject.referral;
    this.assessmentScore = subject.assessmentScore;
    this.assessmentThreshold = subject.assessmentThreshold;
    this.assessmentReviewStatus = subject.assessmentReviewStatus;
    this.hasCasePreparationFile = subject.hasCasePreparationFile || false;

    this.hasLocation = subject.hasLocation || false;
    this.latitude = subject.latitude;
    this.longitude = subject.longitude;
    this.locationChecked = subject.locationChecked;
    this.linkedLocations = subject.linkedLocations || [];
  }

  private summaryTracker: SummaryTracker;
  getSummary(layout: Layout, forceReCalc?: boolean) {
    // check if we already have a summary tracker defined and which matches the current subjecttype and category
    if (
      forceReCalc === true ||
      !this.summaryTracker ||
      !this.summaryTracker.match(this)
    ) {
      // either summary tracker not instantiated, or the subjecttype or category don't match - re-create summary tracker by calling getMetadata
      // we need to do this the long winded way as we need the calculated display values which are also retrieved during getMetadata
      // we could stash the display values, but we would still need to call getMetadata, so let's just go with stashing the bits we need for now :).
      this.getMetadata(layout);
    }
    return this.summaryTracker ? this.summaryTracker.summary : this.reference;
  }

  protected buildSummary(layout: Layout, values: any[]) {
    let summary = '';
    if (layout.summary) {
      const tags = JSON.parse(layout.summary);
      if (Array.isArray(tags) && tags.length > 0) {
        tags.forEach((item) => {
          // search the values array for a matching entry
          let val = values.find(
            (x) => x.id.toString() === item.name.toString()
          );
          if (val) {
            // result array has a matching entry, include the display value in the summary
            summary = summary + val.displayValue;
          } else {
            // if we get here there ie either no value for the field, or the suummary tag is not a field...
            val = layout.subjectType.dataFields.find(
              (x) => x.id.toString() === item.name.toString()
            );
            if (!val) {
              // not a field, so add the label to the summary
              summary = summary + item.label;
            }
          }
          // summary = summary + ((val) ? val.displayValue : item.label);
        });
      }
    }
    if (!summary) {
      // no summary tags found, so use reference
      summary = this.reference;
    }
    // now stash the summary for future reference
    this.summaryTracker = new SummaryTracker(
      summary,
      this.categoryId,
      this.subjectTypeId
    );

    // ...and return
    return {
      id: layout.subjectType.getFieldId('summary'),
      value: summary,
      displayValue: summary,
      type: 'text',
    };
  }

  get url() {
    return this.isReferral
      ? `/referrals/referrals/subject/${this.uid}`
      : `/subjects/${this.subjectType.name}/${this.uid}`;
  }

  get primaryLink() {
    if (
      this.category &&
      this.category.primaryRelationshipId &&
      this.subjectLinks.length > 0
    ) {
      if (this.category.primaryRelationship) {
        const relationships = this.category.primaryRelationshipId.split(',');
        const links = [];
        relationships.forEach((relationshipId) => {
          const link = this.subjectLinks.find(
            (i) => i.relationship.id === Number(relationshipId)
          );
          if (link && link.linkedSubject) {
            links.push(link.linkedSubject.summary);
          }
        });
        return links.join(',');
      }
    }
    return undefined;
  }

  getMetadata(layout: Layout): any[] {
    const result = [];
    const subjectType = layout.subjectType;

    this.dfdeadlineDaysLeftId = subjectType.getFieldId('deadlineDaysLeft');

    // start by iterating over the fixed fields in the subject
    result.push({
      id: subjectType.getFieldId('reference'),
      value: this.reference,
      displayValue: this.reference,
      type: 'text',
    });
    result.push({
      id: subjectType.getFieldId('categoryId'),
      value: this.categoryId,
      displayValue: this.category ? this.category.description : '',
      type: 'category',
    });
    result.push({
      id: subjectType.getFieldId('labels'),
      value: this.labels,
      displayValue: this.labels,
      type: 'text',
    });
    result.push({
      id: subjectType.getFieldId('dateCreated'),
      value: this.dateCreated,
      displayValue: dayjs
        .utc(this.dateCreated || dayjs())
        .format('DD/MMM/YYYY'),
      type: 'date',
    });
    result.push({
      id: subjectType.getFieldId('retentionDate'),
      value: this.retentionDate,
      displayValue: dayjs
        .utc(this.retentionDate || dayjs())
        .format('DD/MMM/YYYY'),
      type: 'date',
    });
    result.push({
      id: subjectType.getFieldId('retentionId'),
      value: this.retentionId,
      displayValue: this.retentionId,
    });
    result.push({
      id: subjectType.getFieldId('createdById'),
      value: this.createdById,
      displayValue: this.createdBy
        ? this.createdBy.fullName || new User(this.createdBy).fullName
        : '',
      type: 'user',
    });

    result.push({
      id: subjectType.getFieldId('stateId'),
      value: this.stateId,
      displayValue: this.stateId,
    });
    result.push({
      id: subjectType.getFieldId('userId'),
      value: this.subjectUsers.length > 0 ? this.subjectUsers[0].userId : '',
      displayValue:
        this.subjectUsers &&
        this.subjectUsers.length > 0 &&
        this.subjectUsers[0].user
          ? this.subjectUsers[0].user.fullName ||
            new User(this.subjectUsers[0].user).fullName ||
            ''
          : '',
      type: 'user',
    });
    result.push({
      id: subjectType.getFieldId('managerId'),
      value: this.subjectUsers.length > 0 ? this.subjectUsers[0].managerId : '',
      displayValue:
        this.subjectUsers &&
        this.subjectUsers.length > 0 &&
        this.subjectUsers[0].manager
          ? this.subjectUsers[0].manager.fullName ||
            new User(this.subjectUsers[0].manager).fullName ||
            ''
          : '',
      type: 'user',
    });
    result.push({
      id: subjectType.getFieldId('organisationId'),
      value:
        this.subjectOrganisations.length > 0
          ? this.subjectOrganisations[0].organisationId
          : '',
      displayValue:
        this.subjectOrganisations &&
        this.subjectOrganisations.length > 0 &&
        this.subjectOrganisations[0].organisation
          ? this.subjectOrganisations[0].organisation.name
          : '',
      type: 'organisation',
    });

    // check we have subject data fields...
    if (!this.subjectData.data) {
      let s = '{';
      subjectType.dataFields.forEach((item) => {
        if (SUBJECTFIXEDFIELDS.indexOf(item.name.toLowerCase()) < 0) {
          s += `"${item.id}":null,`;
        }
      });
      if (s.length > 1) {
        s = s.slice(0, -1);
      }
      this.subjectData.data = JSON.parse(s + '}');
      // if we didn't have data then we certainly won't have corresponding displayValues...
      this.subjectData.displayValues = this.subjectData.data;
    }

    // now iterate over subject data fields - the subject type gives us the possible fields
    subjectType.dataFields.forEach((item) => {
      if (SUBJECTFIXEDFIELDS.indexOf(item.name.toLowerCase()) < 0) {
        const itemData = this.subjectData.data[item.id];
        let displayValue = itemData;
        if (
          itemData ||
          itemData === false ||
          itemData === 0 ||
          itemData == ''
        ) {
          const val = this.subjectData.displayValues[item.id];
          switch (item.dataType.toLowerCase()) {
            case 'currency':
              displayValue = new CurrencyPipe('en-GB').transform(
                val,
                'GBP',
                'symbol',
                '1.2'
              );
              break;
            case 'date':
              displayValue = dayjs
                .utc(val)
                .add(2, 'hours')
                .format('DD/MMM/YYYY');
              break;
            case 'yesno':
              {
                var v= val.toLocaleLowerCase().trim();
                if (v === 'true' || v==='yes') displayValue = 'Yes';
                else if (v === 'false' || v=='no' ) displayValue = 'No';
                else displayValue = ' - ';
              }
              break;
            default:
              if (itemData !== 0) displayValue = val;
              break;
          }
          result.push({
            id: item.id,
            value: itemData,
            displayValue,
            type: item.dataType.toLowerCase(),
          });
        }
      }
    });

    // ..and finally build the summary now that we have values for all available fields
    result.push(this.buildSummary(layout, result));
    return result;
  }

  setFromMetadata(layout: Layout, metadata: any[]) {
    const subjectType = layout.subjectType;
    // console.log('Metadata:', metadata);
    // set fixed fields from metadata
    this.reference = this.getMetadataValue(
      'reference',
      subjectType,
      metadata,
      this.reference
    );
    this.categoryId = this.getMetadataValue(
      'categoryId',
      subjectType,
      metadata,
      this.categoryId
    );
    this.labels = this.getMetadataValue(
      'labels',
      subjectType,
      metadata,
      this.labels
    );
    this.createdById = this.getMetadataValue(
      'createdById',
      subjectType,
      metadata,
      this.createdById
    );
    this.dateCreated = this.getMetadataValue(
      'datecreated',
      subjectType,
      metadata,
      this.dateCreated
    );
    this.retentionDate = this.getMetadataValue(
      'retentionDate',
      subjectType,
      metadata,
      this.retentionDate
    );
    this.retentionId = this.getMetadataValue(
      'retentionId',
      subjectType,
      metadata,
      this.retentionId
    );
    this.stateId = this.getMetadataValue(
      'stateId',
      subjectType,
      metadata,
      this.stateId
    );

    // set the subject data fields - the subject type gives us the possible fields
    subjectType.dataFields.forEach((item) => {
      if (SUBJECTFIXEDFIELDS.indexOf(item.name.toLowerCase()) < 0) {
        const itemData = this.subjectData.data[item.id];
        const index = metadata.findIndex((meta) => meta.id === item.id);
        if (index > -1 || itemData || itemData === false || itemData === 0) {
          // existing value or incoming value specified, set the subjectData accordingly
          this.subjectData.data[item.id] =
            index < 0 ? itemData : metadata[index].value;
        }
      }
    });

    subjectType.dataFields
      .filter((i) => i.dataType.toLowerCase() === 'calculated')
      .forEach((item) => {
        const field1 =
          item.field1 === '0'
            ? undefined
            : subjectType.dataFields.find((i) => i.uid === item.field1);
        const field2 =
          item.field2 === '0'
            ? undefined
            : subjectType.dataFields.find((i) => i.uid === item.field2);
        const val1 = field1 ? this.subjectData.data[field1.id] : item.value1;
        const val2 = field2 ? this.subjectData.data[field2.id] : item.value2;
        if ((val1 || val1 === 0) && (val2 || val2 === 0)) {
          let result = 0;
          switch (item.operator.toLowerCase()) {
            case 'divide':
              result = val1 !== 0 && val2 !== 0 ? val1 / val2 : 0;
              break;
            case 'minus':
              result = val1 - val2;
              break;
            case 'multiply':
              result = val1 * val2;
              break;
            case 'plus':
              result = val1 + val2;
              break;
          }
          const num = Number((Math.abs(result) * 100).toPrecision(15));
          this.subjectData.data[item.id] =
            (Math.round(num) / 100) * Math.sign(result);
        } else {
          this.subjectData.data[item.id] = undefined;
        }
      });

    // check if organisation has been specified
    const organisationId = this.getMetadataValue(
      'organisationId',
      subjectType,
      metadata,
      this.subjectOrganisations.length > 0
        ? this.subjectOrganisations[0].organisationId
        : ''
    );

    if (organisationId && this.subjectOrganisations.length === 0) {
      this.subjectOrganisations.push(
        new SubjectOrganisation({ organisationId })
      );
    }
    if (this.subjectOrganisations.length > 0) {
      if (organisationId !== this.subjectOrganisations[0].organisationId) {
        this.subjectOrganisations[0].organisationId = organisationId;
      }
    }

    // check if user or manager has been specified
    const userId = this.getMetadataValue(
      'userId',
      subjectType,
      metadata,
      this.subjectUsers.length > 0 ? this.subjectUsers[0].userId : ''
    );
    const managerId = this.getMetadataValue(
      'managerId',
      subjectType,
      metadata,
      this.subjectUsers.length > 0 ? this.subjectUsers[0].managerId : ''
    );

    const hasUserOrManager = userId || managerId;
    const changed =
      (this.subjectUsers.length === 0 && hasUserOrManager) ||
      (this.subjectUsers.length > 0 &&
        (userId !== this.subjectUsers[0].userId ||
          managerId !== this.subjectUsers[0].managerId));

    if (changed) {
      if (hasUserOrManager) {
        if (this.subjectUsers.length === 0) {
          this.subjectUsers.push(new SubjectUser());
        }
      }
      const subjectUser = this.subjectUsers[0];
      subjectUser.subjectId = this.id;
      // populate field if relevant property changed
      if (userId !== subjectUser.userId) {
        subjectUser.userId = userId;
      }
      if (managerId !== subjectUser.managerId) {
        subjectUser.managerId = managerId;
      }
    }
  }

  protected getMetadataValue(
    name: string,
    subjectType: SubjectType,
    metadata: any[],
    currentValue: any
  ): any {
    const fieldId = subjectType.getFieldId(name);
    const index = metadata.findIndex((item) => item.id === fieldId);
    return index < 0 ? currentValue : metadata[index].value;
  }

  getDataFieldValue(datafieldName: string, getRaw: boolean = false): any {
    if (datafieldName) {
      if (!this.subjectType) {
        console.error(
          'Error in subject.ts/getDataFieldValue: subjectType is undefined but need to get value for ' +
            datafieldName
        );
        return undefined;
      } else {
        // We need to new up a new SubjectType object as this.subjectType may not be an object of SubjectType.
        // We need SubjectType as it has a method we rely on.
        var st: SubjectType = new SubjectType(this.subjectType);
        const fieldId = st?.getFieldId(datafieldName);
        if (getRaw === false)
          return fieldId > 0 &&
            this.subjectData.displayValues &&
            this.subjectData.displayValues[fieldId]
            ? this.subjectData.displayValues[fieldId]
            : undefined;
        else
          return fieldId > 0 &&
            this.subjectData.data &&
            this.subjectData.data[fieldId]
            ? this.subjectData.data[fieldId]
            : undefined;
      }
    } else {
      return undefined;
    }
  }

  public getDeadlineDaysLeft(): number {
    if (this.dfdeadlineDaysLeftId > 0) {
      return (
        this.subjectData.displayValues[this.dfdeadlineDaysLeftId] || undefined
      );
    }

    var s = this.getDataFieldValue('deadlineDaysLeft');
    if (!s) return 99999;
    return s;
  }

  public static getDeadlineText(daysLeft: number = 99999): string {
    if (daysLeft >= 99999) return '';
    if (daysLeft > 7) return 'Deadline due in more than 7 days';
    else if (daysLeft > 0) return 'Deadline due in ' + daysLeft + ' days';
    else if (daysLeft == 0) return 'Deadline due today';
    else return 'Deadline overdue by  ' + -1 * daysLeft + ' day(s)';
  }
}

// tslint:disable-next-line:max-classes-per-file
export class SubjectData implements IEntity {
  id: number;
  uid: string;
  subjectId: number;
  subjectTypeId: number;
  data: any;
  // separate property to mirror the data property, but replacing any lookup ids with lookup descriptions
  // we are using a separate property because if we modify the data property we are going to have to clean it all up prior to saving/update
  // and that is going to get complicated!!!
  displayValues: any;
}

// tslint:disable-next-line: max-classes-per-file
export class SubjectAssessment {
  id: number;
  subjectId: number;
  assessmentId: number;
  assessment: Assessment;

  constructor(subjectAssessment?) {
    subjectAssessment = subjectAssessment || {};
    this.id = subjectAssessment.id;
    this.subjectId = subjectAssessment.subjectId;
    this.assessmentId = subjectAssessment.assessmentId;
    this.assessment = subjectAssessment.assessment;
  }
}

// tslint:disable-next-line: max-classes-per-file
export class SubjectMarker {
  id: number;
  subjectId: number;
  markerId: number;
  marker: Marker;

  constructor(subjectMarker?) {
    subjectMarker = subjectMarker || {};
    this.id = subjectMarker.id;
    this.subjectId = subjectMarker.subjectId;
    this.markerId = subjectMarker.markerId;
    this.marker = subjectMarker.marker;
  }
}

// tslint:disable-next-line: max-classes-per-file
export class SubjectOrganisation {
  id: number;
  subjectId: number;
  organisationId: number;
  organisation: Organisation;

  constructor(subjectOrganisation?) {
    subjectOrganisation = subjectOrganisation || {};
    this.id = subjectOrganisation.id;
    this.subjectId = subjectOrganisation.subjectId;
    this.organisationId = subjectOrganisation.organisationId;
    this.organisation = subjectOrganisation.organisation;
  }
}

// tslint:disable-next-line: max-classes-per-file
export class SubjectReferral {
  id: number;
  subjectId: number;
  referralId: number;
  referral: Referral;

  constructor(subjectReferral?) {
    subjectReferral = subjectReferral || {};
    this.id = subjectReferral.id;
    this.subjectId = subjectReferral.subjectId;
    this.referralId = subjectReferral.referralId;
    this.referral = subjectReferral.referral;
  }
}

// tslint:disable-next-line: max-classes-per-file
export class SubjectReview {
  id: number;
  subjectId: number;
  reviewId: number;
  review: Review;

  constructor(subjectReview?) {
    subjectReview = subjectReview || {};
    this.id = subjectReview.id;
    this.subjectId = subjectReview.subjectId;
    this.reviewId = subjectReview.reviewId;
    this.review = subjectReview.review;
  }
}

// tslint:disable-next-line:max-classes-per-file
export class SubjectTemplate {
  id: number;
  subjectId: number;
  templateId: number;
  template: Template;
}

// tslint:disable-next-line:max-classes-per-file
export class SubjectUser {
  id: number;
  subjectId: number;
  userId: number;
  user: User;
  managerId: number;
  manager: User;
}

// tslint:disable-next-line:max-classes-per-file
export class SummaryTracker {
  summary: string;
  categoryId: number;
  subjectTypeId: number;

  constructor(summary: string, categoryId: number, subjectTypeId: number) {
    this.summary = summary;
    this.categoryId = categoryId;
    this.subjectTypeId = subjectTypeId;
  }

  match(subject: Subject) {
    if (
      subject &&
      subject.subjectTypeId &&
      subject.subjectTypeId === this.subjectTypeId
    ) {
      // if got here then subject type matches - now check category
      if (subject.categoryId || this.categoryId) {
        if (subject.categoryId && !this.categoryId) {
          return false;
        }
        if (this.categoryId && !subject.categoryId) {
          return false;
        }
        if (subject.categoryId === this.categoryId) {
          return true;
        }
      } else {
        // neither have categoryId specified - so is a match, so return true
        return true;
      }
    }
    // problem with subject or subject type, return false
    return false;
  }
}

export class LinkedLocation {
  subjectId: number;
  linkedSubjectId: number;
  latitude: number;
  longitude: number;
  summary: string;

  constructor(s: Subject) {
    if (s) {
      this.subjectId = s.id;
      this.latitude = s.latitude;
      this.longitude = s.longitude;
      this.summary = s.summary;
    }
  }
}
